import React, { useState, useEffect } from 'react'
import {
  Modal,
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { fetchIndividualStats } from '../../../redux/corporateService'
import CircularProgress from '@mui/material/CircularProgress'
import { useAppDispatch } from '../../../redux/storeExports'

interface SuccessLoginModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  setMuid: (value: string | null) => void
  muid: any
}

type BrokerStats = {
  broker: string
  new: number
  removed: number
  optout_in_progress: number
  optout_submitted: number
  clean_scans: number
}

const IndividualUserStats: React.FC<SuccessLoginModalProps> = ({ open, setOpen, muid, setMuid }) => {
  const [page, setPage] = useState(1)
  const rowsPerPage = 12
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<BrokerStats[] | null>(null)
  const dispatch = useAppDispatch()
  const [paginatedData, setPaginatedData] = useState<BrokerStats[] | null>(null)
  const [totalPages, setTotalPages] = useState(0)

useEffect(() => {
  const fetchEmployeeUsers = async () => {
    try {
      setIsLoading(true)
      setPaginatedData(null)
      const response = await dispatch(fetchIndividualStats(muid)).unwrap()
      const allData: BrokerStats[] = response || []
      setData(allData)
      if (allData.length > 0) {
        const pages = Math.ceil(allData.length / rowsPerPage)
        setTotalPages(pages)
        const startIndex = (page - 1) * rowsPerPage
        const endIndex = startIndex + rowsPerPage
        setPaginatedData(allData.slice(startIndex, endIndex))
      } else {
        setPaginatedData([])
      }
    } catch (e) {
      console.log('error fetching users: ', e)
      setPaginatedData([])
    } finally {
      setIsLoading(false)
    }
  }
  if (open && muid) {
    fetchEmployeeUsers()
  }
}, [open, muid, dispatch, page])

useEffect(() => {
  if (data && data.length > 0) {
    const startIndex = (page - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    setPaginatedData(data.slice(startIndex, endIndex))
  }
}, [page, data])
  const handleClose = () => {
    setOpen(false)
    setPaginatedData(null)
    setPage(1)
    setMuid(null)
    setTotalPages(1)
  }

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1)
  }

  const handleNext = () => {
    if (page < totalPages) setPage(page + 1)
  }

  const handlePageClick = (pageNum: number) => {
    setPage(pageNum)
  }

  const getPageNumbers = () => {
    const pages = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      } else if (page >= totalPages - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        pages.push('...')
        pages.push(page - 1)
        pages.push(page)
        pages.push(page + 1)
        pages.push('...')
        pages.push(totalPages)
      }
    }

    return pages
  }

  return (
    <Modal
      open={open}
      sx={{
        backdropFilter: 'blur(10px)',
        background: 'rgba(0, 0, 0, 0.1)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '81.06rem',
          maxWidth: '90vw',
          borderRadius: '20px',
          opacity: 1,
          padding: '30px',
          gap: '30px',
          bgcolor: 'background.paper',
          '@media (max-width:1300px)': {
            width: '80%',
          },
        }}
      >
        <Typography
          sx={{
            minWidth: '11.06rem',
            height: '2.125rem',
            fontWeight: 700,
            fontStyle: 'normal',
            fontSize: '1.625rem',
            lineHeight: '2.125rem',
            mb: '30px',
            letterSpacing: 0,
            opacity: 1,
          }}
        >
          Broker Report
        </Typography>

        <Box
          sx={{
            width: '100%',
            borderRadius: '0.625rem',
            transform: 'rotate(0deg)',
            opacity: 1,
            overflow: 'hidden',
            overflowX: 'auto',
            mb: '30px',
          }}
        >
          <Table
            sx={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              border: '1px solid #E0E0E0',
              borderRadius: '0.625rem',
              overflow: 'hidden',
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  height: '2.9375rem',
                  backgroundColor: '#FAFAFA',
                  borderBottom: '1px solid #E0E0E0',
                }}
              >
                <TableCell
                  sx={{
                    padding: '0.9375rem',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: 1,
                    letterSpacing: 0,
                    borderBottom: '1px solid #E0E0E0',
                  }}
                >
                  Broker
                </TableCell>
                <TableCell
                  sx={{
                    padding: '0.9375rem',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: 1,
                    letterSpacing: 0,
                    borderBottom: '1px solid #E0E0E0',
                  }}
                >
                  Records Found
                </TableCell>
                <TableCell
                  sx={{
                    padding: '0.9375rem',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: 1,
                    letterSpacing: 0,
                    borderBottom: '1px solid #E0E0E0',
                  }}
                >
                  Removed
                </TableCell>
                <TableCell
                  sx={{
                    padding: '0.9375rem',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: 1,
                    letterSpacing: 0,
                    borderBottom: '1px solid #E0E0E0',
                  }}
                >
                  Removing
                </TableCell>
                <TableCell
                  sx={{
                    padding: '0.9375rem',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: 1,
                    letterSpacing: 0,
                    borderBottom: '1px solid #E0E0E0',
                  }}
                >
                  Opt-out Submitted
                </TableCell>
                <TableCell
                  sx={{
                    padding: '0.9375rem',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: 1,
                    letterSpacing: 0,
                    borderBottom: '1px solid #E0E0E0',
                  }}
                >
                  Clean Scans
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody sx={{ minHeight: '8rem !important' }}>
              {muid === null || muid === undefined ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    User not active
                  </TableCell>
                </TableRow>
              ) : isLoading || paginatedData === null ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <div
                      style={{
                        height: '10vh',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <CircularProgress color="info" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No data available
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, idx) => (
                  <TableRow
                    key={row.broker + idx}
                    sx={{
                      height: '2.759rem',
                      opacity: 1,
                    }}
                  >
                    <TableCell
                      sx={{
                        padding: '0.9375rem',
                        fontWeight: 600,
                        fontStyle: 'normal',
                        fontSize: '0.875rem',
                        lineHeight: '0.875rem',
                        letterSpacing: 0,
                        borderBottom: '1px solid #E0E0E0',
                        // textTransform: 'capitalize',
                      }}
                    >
                      {row.broker}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: '0.9375rem',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '0.875rem',
                        lineHeight: '0.875rem',
                        letterSpacing: 0,
                        borderBottom: '1px solid #E0E0E0',
                      }}
                    >
                      {row.new}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: '0.9375rem',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '0.875rem',
                        lineHeight: '0.875rem',
                        letterSpacing: 0,
                        borderBottom: '1px solid #E0E0E0',
                      }}
                    >
                      {row.removed}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: '0.9375rem',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '0.875rem',
                        lineHeight: '0.875rem',
                        letterSpacing: 0,
                        borderBottom: '1px solid #E0E0E0',
                      }}
                    >
                      {row.optout_in_progress}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: '0.9375rem',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '0.875rem',
                        lineHeight: '0.875rem',
                        letterSpacing: 0,
                        borderBottom: '1px solid #E0E0E0',
                      }}
                    >
                      {row.optout_submitted}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: '0.9375rem',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '0.875rem',
                        lineHeight: '0.875rem',
                        letterSpacing: 0,
                        borderBottom: '1px solid #E0E0E0',
                      }}
                    >
                      {row.clean_scans}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', sm: 'center' },
            gap: { xs: 2, sm: 0 },
            mt: { xs: 2, sm: 0 },
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              width: { xs: '100%', sm: '9.4375rem' },
              height: '2.8125rem',
              borderRadius: '0.625rem',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1.125rem',
              lineHeight: '1.75rem',
              letterSpacing: '0.01em',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Close
          </Button>
          {totalPages > 1 && (
            <Box
              sx={{
                width: { xs: '100%', sm: '485px' },
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-end' },
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: { xs: '10px', sm: '20px' },
                }}
              >
                {/* Previous Button */}
                <IconButton
                  onClick={handlePrevious}
                  disabled={page === 1}
                  sx={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '100px',
                    padding: '5px',
                    border: '1px solid #0000000D',
                    opacity: 1,
                    '&:disabled': {
                      opacity: 0.4,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  <ChevronLeft sx={{ width: '20px', height: '20px' }} />
                </IconButton>

                {/* Page Numbers */}
                {getPageNumbers().map((pageNum, idx) =>
                  pageNum === '...' ? (
                    <Box
                      key={`ellipsis-${idx}`}
                      sx={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        color: '#666',
                      }}
                    >
                      ...
                    </Box>
                  ) : (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageClick(pageNum as number)}
                      sx={{
                        width: '30px',
                        height: '30px',
                        minWidth: '30px',
                        borderRadius: '100px',
                        padding: '5px 10px',
                        border: '1px solid #E0E0E0',
                        backgroundColor: page === pageNum ? '#3F52FF' : 'transparent',
                        color: page === pageNum ? '#fff' : '#333',
                        '&:hover': {
                          backgroundColor: page === pageNum ? '#3F52FF' : '#f5f5f5',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: page === pageNum ? 600 : 400,
                          fontSize: '12px',
                          textTransform: 'none',
                          color: page === pageNum ? '#fff' : '#333',
                        }}
                      >
                        {pageNum}
                      </Typography>
                    </Button>
                  )
                )}

                {/* Next Button */}
                <IconButton
                  onClick={handleNext}
                  disabled={page === totalPages}
                  sx={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '100px',
                    padding: '5px',
                    border: '1px solid #0000000D',
                    opacity: 1,
                    '&:disabled': {
                      opacity: 0.4,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  <ChevronRight sx={{ width: '20px', height: '20px' }} />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

export default IndividualUserStats